import { useMsal } from '@azure/msal-react';
import React, { useEffect, useState } from 'react';
import { deleteCookie, setCookie } from '../Utils/CookieUtils';
import { EventType } from '@azure/msal-browser';
import { COOKIE_IDS } from '../Constants/Constants';

export const AppContext = React.createContext<any>({
  undefined,
});
/**
 * @module ContextProvider
 * @description Provides application-wide context for theme, mobile state, module data, and logout functionality.
 * @param {Object} props - Component properties.
 * @param {React.ReactNode} props.children - The child components to render within the context.
 * @returns {JSX.Element} The ContextProvider component.
 */
export const ContextProvider: React.FC<any> = ({ children }) => {
  const { instance } = useMsal();
  const selectedTheme = localStorage.getItem('selectedTheme');
  const [theme, setTheme] = useState(selectedTheme ?? 'light');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1023);
  const [module, setModule] = useState({});
  const [preferencesClicked, setPreferencesClicked] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1023);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /**
   * Handles the logout process and performs necessary cleanup actions.
   *
   * @param {boolean} [isPasswordReset=false] - Optional flag indicating if the logout is due to a password reset.
   */
  const AppLogout = (isPasswordReset?: boolean) => {
    /**
     * Adds an event callback to handle different event types.
     *
     * @param {Object} event - The event object.
     * @param {string} event.eventType - The type of the event.
     */
    instance.addEventCallback((event: any) => {
      switch (event?.eventType) {
        case EventType.LOGOUT_SUCCESS:
          deleteCookie('id_token');
          deleteCookie('unique_id');
          deleteCookie('session_info');
          deleteCookie('authProviderObjectId');
          deleteCookie(COOKIE_IDS?.PREFERRED_TIME_FORMAT);
          localStorage.clear();
          if (isPasswordReset) {
            setCookie('passwordResetTrigger', 'on');
          }
          break;
        default:
          break;
      }
    });
    instance.logout();
  };

  return (
    <AppContext.Provider
      value={{
        theme,
        setTheme,
        isMobile,
        AppLogout,
        module,
        setModule,
        preferencesClicked,
        setPreferencesClicked,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
